import type { AuthenticationStore } from "../../../stores";
import { makeAutoObservable } from "mobx";
import { AttributeShelf, FetchShelf, LoaderShelf } from "../../../shelves";
import type { apiTypes } from "../../../services";
import { api } from "../../../services";
import { DocumentosCopyBiometryAnalysisStatus } from "../../../enum";
import type { AxiosError } from "axios";

export class Store {
	public loader = new LoaderShelf();
	public userOnboardURL = new AttributeShelf("");
	public getFraudAnalysis: FetchShelf<apiTypes.APIResponse<apiTypes.FraudAnalysis>>;
	public postFraudAnalysis: FetchShelf<apiTypes.APIResponse<apiTypes.FraudAnalysis>>;
	public isResendDocument = new AttributeShelf(false);

	constructor(public authenticationStore: AuthenticationStore, onRedirectDocumentAnalysis: VoidFunction) {
		const authToken = authenticationStore.getToken() || "";

		this.postFraudAnalysis = new FetchShelf(
			async () => {
				await api.postFraudAnalysis(authToken, "FGTS");
				return await api.getFraudAnalysis(authToken);
			},
			(response) => this.onPostFraudAnalysisSuccess(response),
			() => {
				this.isResendDocument.set(true);
			},
		);
		this.getFraudAnalysis = new FetchShelf(
			async () => api.getFraudAnalysis(authToken),
			async (response) => this.onGetFraudAnalysisSuccess(response, onRedirectDocumentAnalysis),
			async (error: AxiosError) => {
				if (error.response && error.response.status === 404) {
					await this.postFraudAnalysis.fetch();
				}
			},
		);

		makeAutoObservable(this);
	}

	public get isLoading(): boolean {
		return this.getFraudAnalysis.loader.isLoading || this.postFraudAnalysis.loader.isLoading;
	}

	public get isResendDocumentValue(): boolean {
		return this.isResendDocument.value;
	}

	public onGetFraudAnalysisSuccess = async (
		fraudAnalysis: apiTypes.APIResponse<apiTypes.FraudAnalysis>,
		onRedirectDocumentAnalysis: VoidFunction,
	): Promise<void> => {
		const { status } = fraudAnalysis.data.documentoscopy;

		if (
			status === DocumentosCopyBiometryAnalysisStatus.NOT_STARTED ||
			status === DocumentosCopyBiometryAnalysisStatus.RESEND_NEEDED
		) {
			if (fraudAnalysis.links && fraudAnalysis.links[0].href) {
				this.userOnboardURL.set(fraudAnalysis.links[0].href);
			} else {
				await this.postFraudAnalysis.fetch();
			}
		} else {
			onRedirectDocumentAnalysis();
		}
	};

	public onPostFraudAnalysisSuccess = (fraudAnalysis: apiTypes.APIResponse<apiTypes.FraudAnalysis>): void => {
		if (fraudAnalysis.links && fraudAnalysis.links[0].href) {
			this.userOnboardURL.set(fraudAnalysis.links[0].href);
		}
	};
}
